import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardSection, Field, Input, Select } from "@/components/ui";
import { adminApi } from "@/lib/api/endpoints";
import { useAsync } from "@/lib/hooks/useAsync";
import { useAuth } from "@/features/auth/AuthContext";
import type { PhotoKind, PurchaseAction } from "@/lib/types/config";
import { useBuilder } from "../BuilderContext";
import { IconCamera, IconDownload, IconLink, IconPrinter, IconSparkles } from "../components/icons";

/**
 * What guests pay, and for what.
 *
 * Money is collected when someone acts on a finished photo — not when they take
 * it. Capture stays free deliberately: a guest who walks away without buying
 * has cost you nothing but a moment of camera time, and charging up front kills
 * the conversion the photo itself would have earned.
 *
 * Prices form a grid because the two products have different costs to you. An
 * AI generation burns real provider credit; a snapshot costs nothing to make.
 */

const KINDS: Array<{ key: PhotoKind; label: string; icon: React.ReactNode; note: string }> = [
  {
    key: "ai_photo",
    label: "AI Photo",
    icon: <IconSparkles size={16} />,
    note: "Costs you provider credit per image, so it usually carries the higher price.",
  },
  {
    key: "snapshot",
    label: "Snapshot",
    icon: <IconCamera size={16} />,
    note: "Free for you to produce. Price it for volume — this is the impulse buy.",
  },
];

const ACTIONS: Array<{ key: PurchaseAction; label: string; icon: React.ReactNode; hint: string }> = [
  { key: "download", label: "Download", icon: <IconDownload size={14} />, hint: "Save the file to their phone." },
  { key: "print", label: "Print", icon: <IconPrinter size={14} />, hint: "A physical copy from the booth." },
  { key: "share", label: "Share link", icon: <IconLink size={14} />, hint: "The QR code target. Often free — it's your marketing." },
];

const CURRENCIES = ["ETB", "USD", "EUR", "GBP", "KES", "NGN"];

export function PricingTab() {
  const { draft, update } = useBuilder();
  const { organization } = useAuth();
  const pricing = draft.pricing ?? {};
  const currency = pricing.currency ?? "ETB";

  // Prices are inert unless the organization can actually take money. Without
  // this check an operator sets a price, sees "Free" on the booth, and has no
  // way to work out why.
  const org = useAsync(
    () => (organization ? adminApi.organizations.get(organization.id) : Promise.reject(new Error("no org"))),
    [organization?.id],
  );
  const paymentsOff = org.data ? org.data.payments_enabled === false : false;


  const anythingPriced = KINDS.some((kind) =>
    ACTIONS.some((action) => (pricing[kind.key]?.[action.key] ?? 0) > 0),
  );

  /**
   * Money commits on blur, not on every keystroke.
   *
   * Config is live: a debounced save while someone types "1000" would briefly
   * publish a price of 0.01. During an event with guests paying, that's a real
   * mispricing window — so prices only leave this tab once the field is done.
   */
  const [editing, setEditing] = useState<Record<string, string>>({});

  const key = (kind: PhotoKind, action: PurchaseAction) => `${kind}.${action}`;

  const priceOf = (kind: PhotoKind, action: PurchaseAction) => (pricing[kind]?.[action] ?? 0) / 100;

  const displayed = (kind: PhotoKind, action: PurchaseAction) =>
    editing[key(kind, action)] ?? priceOf(kind, action).toFixed(2);

  function commit(kind: PhotoKind, action: PurchaseAction) {
    const raw = editing[key(kind, action)];
    setEditing((current) => {
      const next = { ...current };
      delete next[key(kind, action)];
      return next;
    });

    if (raw === undefined) return;

    const cents = Math.max(0, Math.round(Number(raw || 0) * 100));
    if (cents === (pricing[kind]?.[action] ?? 0)) return;

    update((current) => ({
      ...current,
      pricing: {
        ...current.pricing,
        [kind]: { ...current.pricing?.[kind], [action]: cents },
      },
    }));
  }

  return (
    <div className="flex flex-col gap-8">
      <CardSection
        eyebrow="Money"
        title="Pricing"
        description="Guests pay when they act on a finished photo. Configure the payment provider and checkout mode before setting paid prices."
      >
        {paymentsOff && anythingPriced && (
          <div className="flex flex-col gap-2 rounded-lg border border-danger/50 bg-danger/10 px-4 py-3">
            <p className="text-sm font-semibold text-danger">
              These prices are not being charged.
            </p>
            <p className="text-xs leading-relaxed text-haze-200">
              Payments are switched off for your organization, so every action shows as
              <strong> Free</strong> on the booth no matter what you set here. Turn them on to start
              charging — you can use the <em>None</em> provider to test the whole flow without a
              merchant account.
            </p>
            <Link
              to="/admin/organization"
              className="mt-1 inline-flex min-h-10 w-fit items-center rounded-xl border border-danger/30 bg-danger/15 px-4 text-xs font-semibold text-danger hover:border-danger/50 hover:bg-danger/25"
            >
              Open Organization → Payments
            </Link>
          </div>
        )}

        <Card className="flex flex-col gap-5 p-5">
          <div className="grid gap-4 rounded-xl border border-accent/30 bg-accent/5 p-4 sm:grid-cols-2">
            <Field label="Payment provider" hint="Required when any event action has a price.">
              <Select
                value={draft.event?.payment_provider ?? ""}
                onChange={(e) =>
                  update((current) => ({
                    ...current,
                    event: { ...current.event, payment_provider: e.target.value as "telebirr" | "arifpay" | "null" },
                  }))
                }
              >
                <option value="">Select provider</option>
                <option value="telebirr">Telebirr</option>
                <option value="arifpay">ArifPay · Telebirr USSD</option>
                <option value="null">None (test mode)</option>
              </Select>
            </Field>

            {(!draft.event?.payment_provider || draft.event.payment_provider === "telebirr") && <Field label="Telebirr payment mode" hint="Required for paid Telebirr events.">
              <Select
                value={draft.event?.payment_mode ?? ""}
                onChange={(e) =>
                  update((current) => ({
                    ...current,
                    event: { ...current.event, payment_mode: e.target.value as "web_h5" | "superapp" },
                  }))
                }
              >
                <option value="">Select mode</option>
                <option value="web_h5">Web H5 checkout</option>
                <option value="superapp">Telebirr SuperApp</option>
              </Select>
            </Field>}
          </div>

          <Field label="Currency" className="max-w-[200px]">
            <Select
              value={currency}
              onChange={(e) =>
                update((current) => ({
                  ...current,
                  pricing: { ...current.pricing, currency: e.target.value },
                }))
              }
            >
              {CURRENCIES.map((code) => <option key={code} value={code}>{code}</option>)}
            </Select>
          </Field>

          {KINDS.map((kind) => (
            <fieldset key={kind.key} className="flex flex-col gap-4 rounded-xl border border-ink-600 bg-ink-800/60 p-4">
              <legend className="flex items-center gap-2 px-1 text-sm font-semibold text-accent">
                {kind.icon} {kind.label}
              </legend>
              <p className="text-xs leading-relaxed text-haze-400">{kind.note}</p>

              <div className="grid gap-4 sm:grid-cols-3">
                {ACTIONS.map((action) => (
                  <Field
                    key={action.key}
                    label={`${action.label} (${currency})`}
                    hint={action.hint}
                  >
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={displayed(kind.key, action.key)}
                      onChange={(e) =>
                        setEditing((current) => ({
                          ...current,
                          [key(kind.key, action.key)]: e.target.value,
                        }))
                      }
                      onBlur={() => commit(kind.key, action.key)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    />
                  </Field>
                ))}
              </div>
            </fieldset>
          ))}
        </Card>
      </CardSection>

      <CardSection eyebrow="How it works" title="What the guest sees">
        <Card className="flex flex-col gap-3 p-5 text-sm leading-relaxed text-haze-300">
          <p>
            The finished photo appears on the booth screen at full quality — they see exactly what
            they're buying. Each button then shows its price. Tapping one asks for their telebirr
            number and pushes a payment request to their phone; the action unlocks the moment it
            clears.
          </p>
          <p>
            Each action is bought separately. Paying for a print doesn't grant the download, and
            vice versa — so price them as a set, not as alternatives.
          </p>
          <p className="text-haze-400">
            One caveat worth knowing: the photo is shown clean on screen, so on an unattended tablet
            someone could photograph it rather than pay. That's the trade for showing them the real
            thing, and it's the right trade on a kiosk in kiosk mode. If your booths are unattended,
            say so and I'll add a watermarked preview.
          </p>

          {!anythingPriced && (
            <p className="rounded-lg border border-aqua/40 bg-aqua/5 px-3 py-2 text-xs text-aqua">
              Everything is free right now. That's a valid setup — the booth works exactly the same,
              guests just aren't charged.
            </p>
          )}
        </Card>
      </CardSection>
    </div>
  );
}
